import { createStore } from 'zustand/vanilla';
import type { api, TemplateData } from '@stageplottter/core';
import { templatesApi } from './api';
import type { TemplatesApi } from './api';

export interface TemplatesState {
  ws: string | null;
  status: 'idle' | 'loading' | 'ready' | 'error';
  list: api.TemplateSummary[];
  /** Template data by id (loaded right after the list, for placing without waiting). */
  data: ReadonlyMap<string, TemplateData>;
  load(ws: string, force?: boolean): Promise<void>;
  /** Data of one template, fetched if not cached yet. */
  ensure(id: string): Promise<TemplateData>;
  save(ws: string, name: string, data: TemplateData): Promise<api.Template>;
  rename(id: string, name: string): Promise<void>;
  remove(id: string): Promise<void>;
}

/** Templates of the current workspace, shared by the library, the boards page and settings. */
export function createTemplatesStore(
  getApi: () => Pick<TemplatesApi, keyof TemplatesApi> = templatesApi,
) {
  return createStore<TemplatesState>()((set, get) => ({
    ws: null,
    status: 'idle',
    list: [],
    data: new Map(),
    load: async (ws, force = false) => {
      const s = get();
      if (!force && s.ws === ws && s.status !== 'error' && s.status !== 'idle') return;
      set({ ws, status: 'loading', ...(s.ws !== ws ? { list: [], data: new Map() } : {}) });
      try {
        // tolerate an empty answer (204) as 'no templates'
        const list = (await getApi().list(ws)) ?? [];
        if (get().ws !== ws) return;
        set({ list, status: 'ready' });
        const known = get().data;
        const missing = list.filter((x) => !known.has(x.id));
        const full = await Promise.all(
          missing.map((x) =>
            getApi()
              .get(x.id)
              .catch(() => null),
          ),
        );
        if (get().ws !== ws) return;
        const data = new Map(get().data);
        for (const f of full) if (f) data.set(f.id, f.data);
        set({ data });
      } catch {
        if (get().ws === ws) set({ status: 'error' });
      }
    },
    ensure: async (id) => {
      const known = get().data.get(id);
      if (known) return known;
      const full = await getApi().get(id);
      set({ data: new Map(get().data).set(id, full.data) });
      return full.data;
    },
    save: async (ws, name, data) => {
      const tpl = await getApi().create(ws, name, data);
      if (get().ws === ws) {
        const { data: _omit, ...summary } = tpl;
        const list = [...get().list, summary].sort((a, b) => a.name.localeCompare(b.name));
        set({ list, data: new Map(get().data).set(tpl.id, tpl.data) });
      }
      return tpl;
    },
    rename: async (id, name) => {
      const s = await getApi().rename(id, name);
      set({
        list: get()
          .list.map((x) => (x.id === id ? s : x))
          .sort((a, b) => a.name.localeCompare(b.name)),
      });
    },
    remove: async (id) => {
      await getApi().remove(id);
      const data = new Map(get().data);
      data.delete(id);
      set({ list: get().list.filter((x) => x.id !== id), data });
    },
  }));
}

export const templatesStore = createTemplatesStore();
